/**
 * @fileoverview Hook to fetch linked costs for a payment.
 * @module features/payments/hooks/usePaymentLinkedCosts
 */

import { useQuery } from '@tanstack/react-query'
import {
  paymentLinksResponseSchema,
  type PaymentLinksResponse,
} from '@/features/indirect-costs/schemas/indirectCostsSchema'
import apiClient from '@/lib/apiClient'

/**
 * Fetches linked costs for a specific payment from the API.
 *
 * @param paymentId The ID of the payment
 * @returns Promise resolving to payment links response
 */
async function fetchLinkedCosts(paymentId: string): Promise<PaymentLinksResponse> {
  const response = await apiClient.get(`/universal-transactions/${paymentId}/links`)
  return paymentLinksResponseSchema.parse(response.data)
}

/**
 * Hook to fetch linked costs for a specific payment.
 * Uses the new GET /api/universal-transactions/{paymentUuid}/links endpoint.
 *
 * @param paymentId The ID of the payment
 * @returns Query result containing linked costs information
 */
export function usePaymentLinkedCosts(
  paymentId: string | null,
  options?: { enabled?: boolean }
): ReturnType<typeof useQuery<PaymentLinksResponse, Error>> {
  return useQuery<PaymentLinksResponse, Error>({
    queryKey: ['payments', 'linked-costs', paymentId],
    queryFn: () => fetchLinkedCosts(paymentId!),
    enabled: Boolean(paymentId) && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
